import { useCallback } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { CreateAreaPanel } from 'components/create-area-panel/create-area-panel';
import {
  useMapWorkspaceState,
  useResetMapWorkspaceOnUnmount,
} from 'components/create-area-panel/hooks';
import { useGeometryUpload } from 'lib/area-geometry-uploads';
import { activeDatasetTabAtom, allDatasetAtoms } from '../atoms';
import {
  AREAS_DATASET_ID,
  createAreaOpenAtom,
  listCreatedAreaAtom,
} from './atoms';
import { useActiveSelectionSource } from './hooks';

/**
 * Map Query's Create New Area panel (Story 2): the catalog's panel, fed the
 * checked rows of the results tables. A created area appears in the Areas tab,
 * which is brought forward with the new row highlighted.
 *
 * @return {JSX.Element} The panel.
 */
export function MapQueryCreateArea(): JSX.Element {
  const [open, setOpen] = useAtom(createAreaOpenAtom);
  const { drawing, reset, resetAll } = useMapWorkspaceState();
  useResetMapWorkspaceOnUnmount(resetAll);
  const selection = useActiveSelectionSource();
  // Story 4: the boundary files, sent and checked on the server.
  const upload = useGeometryUpload();
  const { set: setUploadFiles } = upload;
  const setActiveTab = useSetAtom(activeDatasetTabAtom);
  const listCreatedArea = useSetAtom(listCreatedAreaAtom);
  const setHighlighted = useSetAtom(
    allDatasetAtoms[AREAS_DATASET_ID].highlightedItemIdAtom
  );

  // Closing abandons the drawing and the files with the rest of the form, which
  // also stops their status reads and channels. A create closes the panel too.
  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
    setUploadFiles([]);
  }, [setOpen, reset, setUploadFiles]);

  const handleCreated = useCallback(
    (area: {
      id: number;
      shared: boolean;
      record: Record<string, unknown>;
    }) => {
      reset();
      listCreatedArea(area);
      setActiveTab(AREAS_DATASET_ID);
      setHighlighted(String(area.id));
    },
    [reset, listCreatedArea, setActiveTab, setHighlighted]
  );

  return (
    <CreateAreaPanel
      open={open}
      onClose={handleClose}
      onCreated={handleCreated}
      drawing={drawing}
      selection={selection}
      upload={upload}
    />
  );
}
